/**
 * ImportRunCard
 * Card for a single import run in the Import History page.
 */

import React from 'react';
import { FileText, User, Clock } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import RulesSnapshotPanel from './RulesSnapshotPanel';

dayjs.extend(relativeTime);

const ImportRunCard = ({ run }) => {
  const createdAt = dayjs(run.created_at);
  const riskBreakdown = run.risk_breakdown || {};

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {/* File name + date */}
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" strokeWidth={1.5} />
            <span className="text-sm font-medium text-slate-900 truncate">
              {run.file_name || 'CSV Upload'}
            </span>
          </div>

          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" strokeWidth={1.5} />
              {createdAt.format('MMM D, YYYY [at] h:mm A')}
              <span className="text-slate-400">({createdAt.fromNow()})</span>
            </span>
            {run.uploaded_by_email && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" strokeWidth={1.5} />
                {run.uploaded_by_email}
              </span>
            )}
          </div>
        </div>

        {/* Patient count + rules version */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-sm font-semibold text-slate-700">
            {run.patient_count || 0} patients
          </span>
          {run.rules_snapshot?.rules_version !== undefined && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-600">
              Rules v{run.rules_snapshot.rules_version}
            </span>
          )}
        </div>
      </div>

      {/* Risk breakdown pills */}
      <div className="px-5 pb-3 flex items-center gap-2">
        {(riskBreakdown.high > 0 || riskBreakdown.medium > 0 || riskBreakdown.low > 0) ? (
          <>
            {riskBreakdown.high > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                {riskBreakdown.high} High
              </span>
            )}
            {riskBreakdown.medium > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600">
                {riskBreakdown.medium} Medium
              </span>
            )}
            {riskBreakdown.low > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600">
                {riskBreakdown.low} Low
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-slate-400 italic">No risk data</span>
        )}

        {run.status && run.status !== 'completed' && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600">
            {run.status}
          </span>
        )}
      </div>

      {/* Rules Snapshot (expandable) */}
      {run.rules_snapshot && (
        <div className="px-5 pb-4">
          <RulesSnapshotPanel rulesSnapshot={run.rules_snapshot} />
        </div>
      )}
    </div>
  );
};

export default ImportRunCard;
